import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, apiError, rupiah, FILE_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { SiteLayout } from "@/site/SiteLayout";
import { SectionTitle, Loading, Empty } from "@/site/parts";
import { toast } from "sonner";
import {
  GraduationCap, Lock, PlayCircle, CheckCircle2, FileText, Award, ChevronRight,
} from "lucide-react";

const COVER = "https://images.unsplash.com/photo-1591604129939-f1efa4d4b0dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function ECourseList() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [access, setAccess] = useState(null);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    api.get("/public/courses").then((r) => setRows(r.data)).catch(() => setRows([]));
    if (user) {
      api.get("/ecourse/access").then((r) => setAccess(r.data)).catch(() => {});
      api.get("/ecourse/my-courses").then((r) => setMine(r.data)).catch(() => {});
    }
  }, [user]);

  const progressOf = (id) => mine.find((m) => m.course_id === id);

  return (
    <SiteLayout>
      <div className="bg-[#0A0A0A] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-6">
          <nav className="flex items-center gap-2 text-xs text-[#8D8677]">
            <Link to="/">Beranda</Link><ChevronRight size={12} /><span className="text-[#D4AF37]">E-Course</span>
          </nav>
          <h1 className="mt-4 font-display text-4xl font-bold text-white sm:text-5xl">E-Course Royal Saudi</h1>
          <p className="mt-4 max-w-2xl text-base text-[#D7D0C2]">
            Kelas manasik, fikih perjalanan, dan persiapan ibadah — gratis untuk jamaah yang
            telah melakukan pembelian di Royal Saudi Haramain.
          </p>
          {user && access && (
            <div data-testid="ecourse-access-banner"
              className={`mt-7 inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${
                access.entitled ? "bg-[#D4AF37]/15 text-[#E5C358]" : "bg-white/5 text-[#D7D0C2]"}`}>
              {access.entitled ? <CheckCircle2 size={16} /> : <Lock size={16} />} {access.note}
            </div>
          )}
          {!user && (
            <Link to="/login" data-testid="ecourse-login-cta"
              className="btn-gold mt-7 inline-block rounded-xl px-6 py-3 text-sm font-bold">
              Masuk untuk Mulai Belajar
            </Link>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6">
        <SectionTitle title="Daftar Kursus"
          subtitle="Setiap kursus berisi modul video, materi bacaan, kuis akhir, dan sertifikat penyelesaian." />
        <div className="mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="course-grid">
          {rows === null ? <Loading /> : rows.length === 0
            ? <Empty label="Belum ada kursus dipublikasikan." />
            : rows.map((c, i) => {
              const pr = progressOf(c.id);
              return (
                <Link key={c.id} to={`/e-course/${c.slug}`} data-testid={`course-card-${c.slug}`}
                  className="card-hover reveal overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white"
                  style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={c.cover_image || COVER} alt={c.title} className="h-full w-full object-cover object-center" loading="lazy" />
                    <span className="absolute left-4 top-4 rounded-full bg-[#0A0A0A]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">
                      {c.level}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg font-semibold text-[#121212]">{c.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm text-[#4A4844]">{c.subtitle || c.description}</p>
                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#8A867C]">
                      <span>{c.module_count} modul</span><span>{c.lesson_count} pelajaran</span>
                      <span>{c.student_count} peserta</span>
                    </div>
                    {pr ? (
                      <div className="mt-4">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F4EFE6]">
                          <div className="h-full bg-[#C5A028]" style={{ width: `${pr.progress_percent}%` }} />
                        </div>
                        <div className="mt-2 text-xs font-semibold text-[#C5A028]">Progres {pr.progress_percent}%</div>
                      </div>
                    ) : (
                      <div className="mt-4 text-sm font-bold text-[#141414]">
                        {c.access_type === "paid" ? rupiah(c.price)
                          : c.access_type === "both" ? `Gratis untuk jamaah · ${rupiah(c.price)} umum`
                            : "Gratis untuk jamaah"}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
        </div>
      </div>
    </SiteLayout>
  );
}

export function ECourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const playerRef = useRef(null);

  const load = useCallback(() => {
    const url = user ? `/ecourse/courses/${slug}` : `/public/courses/${slug}`;
    api.get(url).then((r) => setC(r.data)).catch((e) => setErr(apiError(e)));
  }, [slug, user]);

  useEffect(() => { load(); }, [load]);

  const openLesson = async (l) => {
    if (l.locked) { toast.error("Pelajaran terkunci. Selesaikan syarat akses terlebih dahulu."); return; }
    setActive(l); setVideoUrl("");
    if (l.has_video) {
      try {
        const r = await api.get(`/ecourse/lessons/${l.id}/video-url`);
        setVideoUrl(`${FILE_BASE}${r.data.video_url}`);
      } catch (e) { toast.error(apiError(e)); }
    }
    setTimeout(() => playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const enroll = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try { await api.post(`/ecourse/courses/${c.id}/enroll`); toast.success("Akses kursus dibuka"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const purchase = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/ecourse/courses/${c.id}/purchase`);
      toast.success(`Tagihan ${r.data.invoice.invoice_number} dibuat. Lanjutkan pembayaran manual.`);
      nav("/portal/tagihan");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const markDone = async (l) => {
    try {
      await api.post(`/ecourse/lessons/${l.id}/complete`);
      toast.success("Pelajaran ditandai selesai");
      setActive({ ...l, completed: true });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitQuiz = async () => {
    const qs = c.quiz.questions;
    if (qs.some((q, i) => answers[i] === undefined)) { toast.error("Semua soal wajib dijawab"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/ecourse/courses/${c.id}/quiz/submit`,
        { answers: qs.map((q, i) => answers[i]) });
      setResult(r.data);
      toast[r.data.passed ? "success" : "error"](
        r.data.passed ? `Lulus dengan nilai ${r.data.score}` : `Nilai ${r.data.score}, belum lulus`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (err) return <SiteLayout><Empty label={err} /></SiteLayout>;
  if (!c) return <SiteLayout><Loading /></SiteLayout>;
  const access = c.access || { has_access: false, reason: "login_required", can_purchase: c.access_type !== "entitlement" };
  const enr = c.enrollment;

  return (
    <SiteLayout>
      <div className="relative bg-[#0A0A0A] py-14">
        <img src={c.cover_image || COVER} alt={c.title} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-6">
          <nav className="flex items-center gap-2 text-xs text-[#8D8677]">
            <Link to="/e-course">E-Course</Link><ChevronRight size={12} /><span className="text-[#D4AF37]">{c.level}</span>
          </nav>
          <h1 data-testid="course-title" className="mt-4 max-w-3xl font-display text-3xl font-bold text-white lg:text-5xl">{c.title}</h1>
          <p className="mt-4 max-w-2xl text-base text-[#D7D0C2]">{c.subtitle}</p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm text-[#D7D0C2]">
            <span className="flex items-center gap-2"><GraduationCap size={15} /> {c.lesson_count} pelajaran</span>
            <span className="flex items-center gap-2"><PlayCircle size={15} /> {c.total_minutes} menit</span>
            {c.has_certificate && <span className="flex items-center gap-2"><Award size={15} /> Bersertifikat</span>}
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-12 sm:px-6 lg:grid-cols-[1fr_340px]">
        <div>
          {active && (
            <div ref={playerRef} className="mb-8 rounded-2xl border border-[#E5DEC9] bg-white p-5" data-testid="lesson-player">
              <h2 className="font-display text-xl font-semibold text-[#141414]">{active.title}</h2>
              {videoUrl && (
                <video key={videoUrl} src={videoUrl} controls playsInline data-testid="lesson-video"
                  className="mt-4 w-full rounded-xl bg-black" />
              )}
              {active.has_video && !videoUrl && <p className="mt-3 text-sm text-[#8A867C]">Memuat video...</p>}
              <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[#4A4844]">{active.content}</p>
              {!active.completed && !active.locked && user && access.has_access && (
                <button onClick={() => markDone(active)} data-testid="mark-lesson-done-btn"
                  className="btn-gold mt-5 rounded-xl px-5 py-2.5 text-sm font-bold">Tandai Selesai</button>
              )}
              {active.completed && (
                <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-[#15803D]">
                  <CheckCircle2 size={16} /> Sudah diselesaikan
                </div>
              )}
            </div>
          )}

          <h2 className="font-display text-2xl font-semibold text-[#141414]">Silabus</h2>
          <p className="mt-2 text-sm text-[#4A4844]">{c.description}</p>
          <div className="mt-6 space-y-4" data-testid="course-syllabus">
            {(c.modules || []).map((m, mi) => (
              <div key={m.id} className="overflow-hidden rounded-2xl border border-[#E5DEC9] bg-white">
                <div className="border-b border-[#F4EFE6] bg-[#FBF9F4] px-5 py-3 text-sm font-bold text-[#141414]">
                  Modul {mi + 1} · {m.title}
                </div>
                <ul>
                  {m.lessons.map((l) => (
                    <li key={l.id}>
                      <button onClick={() => openLesson(l)} data-testid={`lesson-item-${l.id}`}
                        className="flex w-full items-center justify-between gap-3 border-b border-[#F4EFE6] px-5 py-3.5 text-left text-sm transition-colors hover:bg-[#FBF9F4]">
                        <span className="flex items-center gap-3">
                          {l.completed ? <CheckCircle2 size={16} className="shrink-0 text-[#15803D]" />
                            : l.locked ? <Lock size={16} className="shrink-0 text-[#8A867C]" />
                              : l.has_video ? <PlayCircle size={16} className="shrink-0 text-[#C5A028]" />
                                : <FileText size={16} className="shrink-0 text-[#C5A028]" />}
                          <span className={l.locked ? "text-[#8A867C]" : "text-[#141414]"}>{l.title}</span>
                          {l.is_preview && <span className="rounded-full bg-[#F4EFE6] px-2 py-0.5 text-[10px] font-bold uppercase text-[#C5A028]">Preview</span>}
                        </span>
                        <span className="shrink-0 text-xs text-[#8A867C]">{l.duration_minutes} mnt</span>
                      </button>
                    </li>
                  ))}
                  {m.lessons.length === 0 && <li className="px-5 py-4 text-sm text-[#8A867C]">Belum ada pelajaran.</li>}
                </ul>
              </div>
            ))}
          </div>

          {c.quiz && (
            <div className="mt-10 rounded-2xl border border-[#E5DEC9] bg-white p-6" data-testid="course-quiz">
              <h2 className="font-display text-2xl font-semibold text-[#141414]">{c.quiz.title}</h2>
              <p className="mt-2 text-sm text-[#4A4844]">Nilai minimal lulus {c.passing_score}.</p>
              <div className="mt-6 space-y-6">
                {c.quiz.questions.map((q, i) => (
                  <div key={q.id}>
                    <div className="text-sm font-semibold text-[#141414]">{i + 1}. {q.question}</div>
                    <div className="mt-3 space-y-2">
                      {q.options.map((o, oi) => (
                        <label key={`${q.id}-${oi}`} className="flex cursor-pointer items-center gap-3 rounded-xl border border-[#E5DEC9] px-4 py-2.5 text-sm hover:border-[#C5A028]">
                          <input type="radio" name={`q-${i}`} checked={answers[i] === oi}
                            onChange={() => setAnswers({ ...answers, [i]: oi })}
                            data-testid={`quiz-${i}-option-${oi}`} />
                          {o}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={submitQuiz} disabled={busy} data-testid="submit-quiz-btn"
                className="btn-gold mt-6 rounded-xl px-6 py-3 text-sm font-bold disabled:opacity-60">
                Kirim Jawaban
              </button>
              {result && (
                <div data-testid="quiz-result" className={`mt-4 rounded-xl p-4 text-sm ${
                  result.passed ? "bg-[#F0FDF4] text-[#15803D]" : "bg-[#FEF2F2] text-[#B91C1C]"}`}>
                  Nilai {result.score} ({result.correct}/{result.total} benar) —
                  {result.passed ? " Selamat, Anda lulus!" : ` minimal ${result.passing_score} untuk lulus.`}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="h-fit rounded-2xl border border-[#E5DEC9] bg-white p-6 luxury-shadow lg:sticky lg:top-28">
          {access.has_access ? (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-[#C5A028]">Akses aktif</div>
              <div className="mt-2 font-display text-2xl font-bold text-[#141414]">
                Progres {enr?.progress_percent || 0}%
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F4EFE6]">
                <div className="h-full bg-[#C5A028]" style={{ width: `${enr?.progress_percent || 0}%` }} />
              </div>
              {!enr && (
                <button onClick={enroll} disabled={busy} data-testid="enroll-btn"
                  className="btn-gold mt-5 w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60">
                  Mulai Kursus
                </button>
              )}
              {enr?.certificate_number && (
                <a href={`${FILE_BASE}/api/ecourse/certificate/${c.id}`} target="_blank" rel="noreferrer"
                  data-testid="certificate-link"
                  className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-[#141414] py-3 text-sm font-semibold text-[#141414]">
                  <Award size={16} /> Unduh Sertifikat
                </a>
              )}
              {enr && !enr.certificate_number && (
                <p className="mt-4 text-xs text-[#8A867C]">
                  Selesaikan semua pelajaran{c.has_quiz ? " dan lulus kuis akhir" : ""} untuk mendapatkan sertifikat.
                </p>
              )}
            </>
          ) : (
            <>
              <div className="text-xs font-bold uppercase tracking-wider text-[#C5A028]">Akses kursus</div>
              <p className="mt-3 text-sm text-[#4A4844]" data-testid="locked-notice">
                {access.reason === "login_required"
                  ? "Masuk terlebih dahulu untuk mengikuti kursus ini."
                  : access.reason === "purchase_required"
                    ? "Kursus ini dijual terpisah. Beli untuk membuka semua materi."
                    : "Kursus ini gratis untuk jamaah dengan minimal satu pembayaran terverifikasi."}
              </p>
              {c.access_type !== "entitlement" && (
                <div className="mt-4 font-display text-2xl font-extrabold text-[#141414]">{rupiah(c.price)}</div>
              )}
              {!user ? (
                <Link to="/login" data-testid="course-login-btn"
                  className="btn-gold mt-5 block rounded-xl py-3 text-center text-sm font-bold">Masuk</Link>
              ) : (
                <>
                  {access.can_purchase && (
                    <button onClick={purchase} disabled={busy} data-testid="purchase-course-btn"
                      className="btn-gold mt-5 w-full rounded-xl py-3 text-sm font-bold disabled:opacity-60">
                      Beli Kursus Ini
                    </button>
                  )}
                  <Link to="/paket-umrah"
                    className="mt-3 block rounded-xl border border-[#141414] py-3 text-center text-sm font-semibold text-[#141414]">
                    Lihat Paket Umrah
                  </Link>
                </>
              )}
            </>
          )}
        </aside>
      </div>
    </SiteLayout>
  );
}
